import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// COMPONENTS
import { trackById } from 'v2/modules/shared/util/track-by.util';

import { DialogConfig } from '../../../shared/configs/dialog.config';
import { PurchaseOrderFacadeService } from '../../purchase-order-facade.service';

import { PaginationConfig } from './../../../shared/configs/paginator.config';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';
import { IPagination } from './../../../shared/interfaces/paginator.interface';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

// CONFIGS

// INTERFACES
import { PurchaseOrderConfig } from './../../configs/purchase-order.config';
import { IPurchaseOrderShipment } from './../../interfaces/purchase-order.interface';

// SERVICES

// UTILS

import { PurchaseOrderShipmentsFormComponent } from './../purchase-order-shipments-form/purchase-order-shipments-form.component';

@Component({
  selector: 'lum-purchase-order-shipments-table',
  templateUrl: './purchase-order-shipments-table.component.html',
  styleUrls: ['./purchase-order-shipments-table.component.scss'],
})
export class PurchaseOrderShipmentsTableComponent implements OnInit, OnDestroy {
  paginationConfig = PaginationConfig;

  private onDestroy$ = new Subject<void>();

  trackById = trackById;

  constructor(
    private snackBar :MatSnackBar,
    private purchaseOrderFacadeService :PurchaseOrderFacadeService,
    private dialog :MatDialog,
  ) {}

  ngOnInit() :void {
    this.purchaseOrderFacadeService.setCurrentQueryParams(
      PurchaseOrderConfig.defaultQueryParamsShipments,
    );
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onEdit(shipment :IPurchaseOrderShipment) :void {
    this.dialog.open(PurchaseOrderShipmentsFormComponent, {
      data: {
        id: shipment.id,
      },
      ...DialogConfig.LARGE,
    });
  }

  onConfirmDelete(id :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Delete Shipment',
          message: 'Are you sure you want to delete this shipment?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.deleteShipment(id);
        }
      });
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.purchaseOrderFacadeService.setCurrentQueryParams({
      ...this.currentQueryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    });
  }

  private deleteShipment(id :number) :void {
    this.purchaseOrderFacadeService
      .deleteShipment(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.snackBar.open('Shipment deleted', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: () => {
          this.snackBar.open('Error deleting shipment', 'Fail');
        },
      });
  }

  get loading$() :Observable<boolean> {
    return this.purchaseOrderFacadeService.loadingShipments$;
  }

  get shipments$() :Observable<IPurchaseOrderShipment[]> {
    return this.purchaseOrderFacadeService.shipments$;
  }

  get currentQueryParams() :any {
    return this.purchaseOrderFacadeService.currentShipmentQueryParams;
  }

  get pagination() :IPagination {
    return this.purchaseOrderFacadeService.pagination;
  }
}
